 /**
  * Hook para verificar acesso a módulos da empresa
  * 
  * Uso:
  * const { hasProduction, canAccessRoute, shouldShowMenuItem } = useModuleAccess();
  * 
  * if (!hasProduction) {
  *   return <ModuleLockedPage moduleId="MODULE_PRODUCTION" />;
  * }
  */
 
 import { useMemo } from 'react';
 import { useQuery } from '@tanstack/react-query';
 import { supabase } from '@/integrations/supabase/client';
 import { useStableAuth } from '@/hooks/useStableAuth';
 import { 
   ModuleId, 
   MODULES_REGISTRY, 
   getModuleForRoute, 
   getModuleForMenuItem 
 } from '@/config/modules';
 
 interface CompanyModule {
   module_id: string;
   is_active: boolean;
   trial_expires_at: string | null;
 }
 
 interface ModuleAccessResult {
   // Status de carregamento
   isLoading: boolean;
   
   // Verificações individuais de módulo
   hasProduction: boolean;
   hasRentals: boolean;
   hasCrmAI: boolean;
   hasPOS: boolean;
   hasFinancialAdvanced: boolean;
   hasLogisticsAdvanced: boolean;
   
   // Helpers genéricos
   hasModule: (moduleId: ModuleId) => boolean;
   canAccessRoute: (routePath: string) => boolean;
   shouldShowMenuItem: (menuItemId: string) => boolean;
   
   // Para o Marketplace
   activeModules: ModuleId[];
   inactiveModules: ModuleId[];
   
   // Trial info
   getTrialInfo: (moduleId: ModuleId) => { isOnTrial: boolean; expiresAt: Date | null };
 }
 
 export const useModuleAccess = (): ModuleAccessResult => {
   const { companyId, loading: authLoading } = useStableAuth();
 
   const { data: companyModules, isLoading: modulesLoading } = useQuery({
     queryKey: ['company-modules', companyId],
     queryFn: async () => {
       if (!companyId) return [];
       
       const { data, error } = await supabase
         .from('company_modules')
         .select('module_id, is_active, trial_expires_at')
         .eq('company_id', companyId)
         .eq('is_active', true);
 
       if (error) throw error;
       return (data || []) as CompanyModule[];
     },
     enabled: !!companyId && !authLoading,
     staleTime: 5 * 60 * 1000, // 5 minutos
   });
 
   const activeModuleIds = useMemo(() => {
     if (!companyModules) return new Set<ModuleId>();
     
     return new Set(
       companyModules
         .filter(m => {
           // Módulo ativo e sem trial expirado
           if (!m.is_active) return false;
          // Usar comparação em milissegundos (UTC absoluto) para evitar problemas de timezone
          if (m.trial_expires_at && new Date(m.trial_expires_at).getTime() < Date.now()) return false;
           return true;
         })
         .map(m => m.module_id as ModuleId)
     );
   }, [companyModules]);
 
   const hasModule = (moduleId: ModuleId): boolean => {
     return activeModuleIds.has(moduleId);
   };
 
   const canAccessRoute = (routePath: string): boolean => {
     const moduleId = getModuleForRoute(routePath);
     // Se não pertence a nenhum módulo, é CORE (sempre acessível)
     if (!moduleId) return true;
     return hasModule(moduleId);
   };
 
   const shouldShowMenuItem = (menuItemId: string): boolean => {
     const moduleId = getModuleForMenuItem(menuItemId);
     // Se não pertence a nenhum módulo, é CORE (sempre visível)
     if (!moduleId) return true;
     return hasModule(moduleId);
   };
 
   const getTrialInfo = (moduleId: ModuleId) => {
     const module = companyModules?.find(m => m.module_id === moduleId);
     if (!module?.trial_expires_at) {
       return { isOnTrial: false, expiresAt: null };
     }
     return {
       isOnTrial: true,
       expiresAt: new Date(module.trial_expires_at),
     };
   };
 
   const activeModules = useMemo(() => Array.from(activeModuleIds), [activeModuleIds]);
   
   const inactiveModules = useMemo(() => {
     const allModules = Object.keys(MODULES_REGISTRY) as ModuleId[];
     return allModules.filter(id => !activeModuleIds.has(id));
   }, [activeModuleIds]);
 
   return {
     isLoading: authLoading || modulesLoading,
     
     // Atalhos para módulos específicos
     hasProduction: hasModule('MODULE_PRODUCTION'),
     hasRentals: hasModule('MODULE_RENTALS'),
     hasCrmAI: hasModule('MODULE_CRM_AI'),
     hasPOS: hasModule('MODULE_POS'),
     hasFinancialAdvanced: hasModule('MODULE_FINANCIAL_ADVANCED'),
     hasLogisticsAdvanced: hasModule('MODULE_LOGISTICS_ADVANCED'),
     
     // Helpers
     hasModule,
     canAccessRoute,
     shouldShowMenuItem,
     
     // Listas
     activeModules,
     inactiveModules,
     
     // Trial
     getTrialInfo,
   };
 };